import type { RngHolder } from '../math/rng';
import type { GameConfigShape, ScaledCounts } from '../config/gameConfig';
import type { BoardGraph, ResolvedMap } from '../board/graph';
import type { EdgeId, HexId, VertexId } from '../board/axial';
import type { GameDef, ModuleId, PieceId, ResourceBag } from './gameDef';
import type { SeatIdx } from '../types';

// GameState (plan §4): ONE serializable, JSON-PURE object — integers, strings, booleans,
// null, arrays, plain objects only; fields never optional (concrete or null); no floats
// (all quantities are counts; rng is a u32). Canonical JSON of state round-trips exactly
// (T-ENG-067); every leaf survives clone/serialize/hash (T-ENG-068, seed D62 technique).

export interface Placement {
  piece: string;
  owner: SeatIdx | 'neutral';
}

export interface SeatState {
  /** Resource cards (public count, private composition — plan §10). */
  hand: ResourceBag;
  /** Permanent auto-pass (plan §13.5, D5); seat stays robbable. */
  resigned: boolean;
  resignedAtSeq: number | null;
}

export interface TurnState {
  /** 1-based; increments when the dice pass (plan §4.1). */
  number: number;
  roller: SeatIdx;
  firstSeat: SeatIdx;
}

// Phase nodes (plan §7.1). Payloads are PUBLIC by construction (T-RED-009).
export type BackRef = 'PRE_ROLL' | 'ACTION' | 'PARTNER_ACTION';
export type PhaseNode =
  | { t: 'SETUP_SETTLEMENT'; seat: SeatIdx; round: 1 | 2 }
  | { t: 'SETUP_ROAD'; seat: SeatIdx; round: 1 | 2; at: VertexId }
  | { t: 'PRE_ROLL'; seat: SeatIdx }
  | { t: 'DISCARD'; seat: SeatIdx; owed: Record<number, number> }
  | { t: 'BANDIT_MOVE'; seat: SeatIdx; via: 'ROLL7' | 'MILITIA'; back: BackRef }
  | { t: 'STEAL_PICK'; seat: SeatIdx; hex: HexId; via: 'ROLL7' | 'MILITIA'; back: BackRef }
  | { t: 'ACTION'; seat: SeatIdx }
  | { t: 'ROADWORKS'; seat: SeatIdx; remaining: number; back: BackRef }
  | { t: 'PARTNER_ACTION'; roller: SeatIdx; seat: SeatIdx }
  | { t: 'SBP'; roller: SeatIdx; queue: SeatIdx[]; seat: SeatIdx }
  /**
   * H20-S4: seats owed a free choice of resource (a gold field paid them). `seat` is who CAUSED
   * it — the roller, the placer, the discoverer — and where the clockwise resolution starts;
   * `resume` is the WHOLE node play continues in (setup must return to a SETUP_ROAD, a
   * discovery mid-Road-Building to its ROADWORKS — a BackRef can name neither). Never nested.
   */
  | { t: 'GOLD_PICK'; seat: SeatIdx; owed: Record<number, number>; resume: PhaseNode }
  | { t: 'GAME_OVER'; winner: SeatIdx };

export interface GameState {
  /** Actions applied so far == next log position (plan §4.1). */
  seq: number;
  /** mulberry32 u32 — the ONE randomness source (plan §9.1). */
  rng: number;
  turn: TurnState;
  phase: PhaseNode;
  seats: SeatState[];
  board: {
    vertices: Record<VertexId, Placement>;
    edges: Record<EdgeId, Placement>;
    hexes: Record<HexId, Placement>;
  };
  /** Shared exhaustible stock (outline §2/§6). */
  supply: ResourceBag;
  /** Module-keyed state bags (plan §4.2/§5.2); absent module = absent key. */
  bags: Record<ModuleId, unknown>;
  winner: SeatIdx | null;
  /** Display standings, set with the winner at GAME_OVER (plan §13.6). */
  standings: SeatIdx[] | null;
}

/** Everything pure functions need alongside the state (plan §5.2 env). Never serialized. */
export interface GameRuntime {
  def: GameDef;
  cfg: GameConfigShape;
  seatsCount: number;
  counts: ScaledCounts;
  map: ResolvedMap;
  board: BoardGraph;
  /** The composed module tables (engine/registry.ts). */
  composed: ComposedModules;
  /**
   * H20-S1: the live-board memo — the derived graphs `liveBoard` has built for this env, keyed
   * by the overlay content that produced them. On the env and never module-level (shared/ holds
   * no global state); absent ⇒ `liveBoard` simply does not cache. Classic games never fill it:
   * with no overlay registered the live board IS `board`.
   */
  liveMemo?: { board: BoardGraph; byKey: Map<string, BoardGraph> };
}

// ── module composition surface (plan §5.2) ───────────────────────────────────

export interface Rejection {
  ok: false;
  /** Fixed template enum — NEVER interpolates hidden values (T-SEC-009). */
  reason: string;
}
export interface Accepted {
  ok: true;
}
export type Verdict = Accepted | Rejection;
export const OK: Accepted = Object.freeze({ ok: true });
export function reject(reason: string): Rejection {
  return { ok: false, reason };
}

/** The public-narrative event under construction (plan §8.4): seq/idx stamped by apply. */
export interface GameEventDraft {
  kind: string;
  vis: 'public' | SeatIdx[];
  /** Dual-visibility payloads (STOLEN's card identity): merged into the event for these
   *  seats by viewFor, stripped for everyone else (plan §8.4; T-RED-005). */
  privateTo?: SeatIdx[];
  privateFields?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface GameAction {
  seat: SeatIdx;
  kind: string;
  payload: Record<string, unknown>;
}

export interface ActionSpec {
  /** Structural payload check (zod on the wire; engine re-checks shape minimally). */
  validate(state: GameState, action: GameAction, env: GameRuntime): Verdict;
  /** Mutates the WORKING CLONE only (applyAction clones first — T-ENG-060). */
  apply(state: GameState, action: GameAction, env: GameRuntime, emit: (e: GameEventDraft) => void): void;
  /**
   * Candidate payloads for legalActions (plan §8.3): an array of payloads to filter
   * through validateAction, a descriptor for open-payload kinds, or null when this seat
   * can never act via this kind right now (cheap pre-filter).
   */
  enumerate(state: GameState, env: GameRuntime, seat: SeatIdx): Array<Record<string, unknown>> | { descriptor: Record<string, unknown> } | null;
}

export interface PhaseStateSpec {
  /** Victory gating (plan §7.5): the seat that may auto-win in this state, or null. */
  eligibleWinner(node: PhaseNode): SeatIdx | null;
  /** §13.4 policy table key — totality checked by T-ENG-064. */
  timeoutKey: string;
  /**
   * A RESPONSE window (H20-S0c): no single seat owns it; it waits on these seats, in
   * ascending order. Present ⇔ the phase is a response window — the hosts run per-seat
   * response clocks and pause the turn clock, the client hands the device to the first owing
   * seat. DISCARD was the only such window and a dozen places knew it by name; registering
   * the shape is what lets a second one exist without any of them learning its name.
   */
  owedSeats?(state: GameState): SeatIdx[];
  /**
   * This phase's §13.4 row (H20-S0c). When registered it answers for the phase and
   * `autoPolicy`'s own table is not consulted — which is how a module's window gets a
   * timeout resolution without the content-blind core naming its verbs.
   */
  policy?(state: GameState, env: GameRuntime, seat: SeatIdx): GameAction | null;
}

/** H20-S0d: evaluation options for a placement rule. */
export interface PlacementOpts {
  /** Treat this edge as empty — how a ship MOVE asks "legal once the ship is lifted" without cloning state. */
  excludeEdge?: EdgeId;
}

/** H20-S0d: "may `seat` put this piece on `site`?", minus cost and pool (outline §14.17's reading). */
export type PlacementRule = (state: GameState, env: GameRuntime, seat: SeatIdx, site: string, opts?: PlacementOpts) => boolean;

/** H20-S1: cells a module has decided mid-game (a fog reveal) — land with its disc, or water. */
export type TerrainOverlay = Record<HexId, { tile: string; disc: number | null } | 'water'>;

/**
 * H20-S1: plan §10.1's `viewBag`, built. `project` returns a JSON-pure view of the module's bag
 * for `seat` (`over` = the PT-7 game-over reveal); `restore` rebuilds a validator-grade bag
 * from it on the client. Every bag a validator reads must project — one that does not is
 * stubbed `{}` client-side, exactly as before.
 */
export interface ViewProjectionSpec {
  project(bag: unknown, seat: SeatIdx, over: boolean): unknown;
  restore(projection: unknown, env: GameRuntime): unknown;
}

export interface BagSpec {
  init(env: Omit<GameRuntime, 'composed'>, rng: RngHolder, moduleCfg: Record<string, unknown>): unknown;
  /** Explicit field-by-field clone (quarry doctrine; completeness proven by T-ENG-068). */
  clone(bag: unknown): unknown;
}

export interface ComposedModules {
  order: ModuleId[];
  actions: ReadonlyMap<string, { module: ModuleId; spec: ActionSpec }>;
  guards: ReadonlyMap<string, Array<{ module: ModuleId; guard: (state: GameState, action: GameAction, env: GameRuntime) => Verdict }>>;
  phaseStates: ReadonlyMap<string, PhaseStateSpec>;
  bags: ReadonlyMap<ModuleId, BagSpec>;
  vpSources: ReadonlyArray<{ module: ModuleId; key: string; hidden: boolean; compute: (state: GameState, env: GameRuntime, seat: SeatIdx) => number }>;
  diceHandlers: ReadonlyArray<{ module: ModuleId; handle: (state: GameState, env: GameRuntime, d1: number, d2: number, emit: (e: GameEventDraft) => void) => 'handled' | 'pass' }>;
  afterAction: ReadonlyArray<{ module: ModuleId; hook: (state: GameState, env: GameRuntime, emit: (e: GameEventDraft) => void) => void }>;
  turnBoundary: ReadonlyArray<{ module: ModuleId; hook: (state: GameState, env: GameRuntime) => void }>;
  windowOpen: ReadonlyArray<{ module: ModuleId; hook: (state: GameState, env: GameRuntime, seat: SeatIdx) => void }>;
  moduleConfig: Readonly<Record<ModuleId, Record<string, unknown>>>;
  /** H20-S0d: piece → its placement rule (ordered override: the last registration wins). */
  placementRules: ReadonlyMap<PieceId, PlacementRule>;
  /** H20-S0d: hooks run by `notePiecePlaced` the moment a piece goes down (discovery lives here). */
  piecePlaced: ReadonlyArray<{ module: ModuleId; hook: (state: GameState, env: GameRuntime, seat: SeatIdx, piece: PieceId, site: string, emit: (e: GameEventDraft) => void) => void }>;
  /** H20-S1: cells decided mid-game, merged in module order by `liveBoard`. */
  terrainOverlays: ReadonlyArray<{ module: ModuleId; overlay: (state: GameState) => TerrainOverlay }>;
  /** H20-S1: bags that project into `GameView.modules` (module order). */
  projections: ReadonlyMap<ModuleId, ViewProjectionSpec>;
}

/**
 * Create a game (implemented in createGame.ts — this file stays types-only): validate def,
 * resolve the map (R/G draws), derive the board, init bags in module order (the advance
 * deck FY is the only bag draw — plan §9.2 L1), draw the first seat (L2), enter
 * SETUP_SETTLEMENT(firstSeat, 1). createGame.ts imports only TYPES from here, so the
 * re-export is cycle-free.
 */
export { createGameImpl as createGame } from './createGame';

/**
 * The runtime WITHOUT a state, from an already-resolved map (H6-S1) — the builder a
 * client uses, since it must never hold the seed that reproduces the hidden deck.
 */
export { envFromResolvedMap } from './createGame';
